/**
 * One agent-proposed human task in full – who, what, why, the draft – with
 * its actions: the primary one (open chat + copy draft / open profile / open
 * link), the close (Done or Sent ✓), Skip (work-through only) and Decline…
 * (reason required). Presentational: the caller owns the writes and passes
 * them in, so the work-through mode can bind the same calls to keys.
 */
import { useState } from "react";
import { Link } from "react-router-dom";

import { OPEN_TASK_STATUSES, type HumanTask } from "../../api/tasks";
import { copyText } from "../../lib/clipboard";
import { ageSeconds, berlinDateTime } from "../../lib/console";
import { taskTypeLabel } from "../../lib/taskGroups";
import { closeLabel, primaryAction } from "../../lib/workThrough";
import { Age } from "../Age";
import { Pill } from "../Pill";
import { PriorityBadge } from "../PriorityBadge";
import { useToast } from "../toast/context";


export function TaskCard({
  task,
  nowMs,
  busy = false,
  onPrimary,
  onClose,
  onDecline,
  onSkip,
  emphasis = false,
}: {
  task: HumanTask;
  nowMs: number;
  busy?: boolean;
  onPrimary: () => void;
  onClose: () => void;
  onDecline: (reason: string) => Promise<boolean>;
  onSkip?: () => void;
  /** The work-through card: larger body, the draft always open. */
  emphasis?: boolean;
}) {
  const toast = useToast();
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState("");
  const [copied, setCopied] = useState(false);
  const open = OPEN_TASK_STATUSES.includes(task.status);
  const primary = primaryAction(task);

  async function copyDraft() {
    if (!task.draft_message) return;
    if (await copyText(task.draft_message)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } else {
      toast.error("Could not copy – select the text and copy it by hand.");
    }
  }

  async function sendDecline() {
    const text = reason.trim();
    if (!text) return;
    if (await onDecline(text)) {
      setReason("");
      setDeclining(false);
    }
  }

  return (
    <div className="panel">
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <span className="font-heading font-semibold text-ink">
          {task.contact_id && task.contact_name ? (
            <Link to={`/contacts/${task.contact_id}`} className="link">
              {task.contact_name}
            </Link>
          ) : (
            task.contact_name || task.title || taskTypeLabel(task.task_type)
          )}
        </span>
        {task.company_name ? <span className="text-sm text-ink-low">{task.company_name}</span> : null}
        <span className="ml-auto flex items-center gap-2">
          {task.priority === "high" ? <PriorityBadge priority={1} /> : null}
          {!open ? <Pill value={task.status} /> : null}
          <Age seconds={ageSeconds(task.created_at, nowMs)} />
        </span>
      </div>
      <div className="text-xs text-ink-low mb-2">
        {taskTypeLabel(task.task_type)}
        {task.agent_slug ? <> · from <span className="font-mono">{task.agent_slug}</span></> : null}
        {task.expires_at ? <> · expires <span className="font-mono">{berlinDateTime(task.expires_at)}</span></> : null}
      </div>

      {task.revision ? (
        <div className={task.revision.released ? "callout callout--warn text-sm mb-2" : "callout callout--info text-sm mb-2"}>
          <div>
            {task.revision.released ? (
              <>Not revised – <span className="font-mono">{task.revision.seat ?? "the seat"}</span> ended the round without a change.</>
            ) : (
              <>
                Being revised by <span className="font-mono">{task.revision.seat ?? "the seat"}</span>
                {task.revision.since ? <> since <span className="font-mono">{berlinDateTime(task.revision.since)}</span></> : null}
                {" "}– you can still send it.
              </>
            )}
            <div className="text-ink-medium whitespace-pre-wrap">Your note: {task.revision.note}</div>
          </div>
        </div>
      ) : null}
      {task.title && task.contact_name ? <div className="text-sm font-semibold text-ink">{task.title}</div> : null}
      {task.description ? <p className="text-sm text-ink-medium mt-1">{task.description}</p> : null}
      {task.details ? (
        <details className="text-sm mt-2">
          <summary className="cursor-pointer text-ink-low">More context</summary>
          <div className="whitespace-pre-wrap mt-1 text-ink-medium">{task.details}</div>
        </details>
      ) : null}

      {task.draft_message ? (
        <div className="mt-3">
          <div className="flex items-baseline justify-between">
            <div className="label mb-0">Draft</div>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => void copyDraft()}>
              {copied ? "Copied" : "Copy text"}
            </button>
          </div>
          <pre
            className={
              emphasis
                ? "input font-sans text-sm leading-relaxed whitespace-pre-wrap mt-1"
                : "input font-sans text-sm leading-relaxed whitespace-pre-wrap max-h-52 overflow-y-auto mt-1"
            }
          >
            {task.draft_message}
          </pre>
        </div>
      ) : null}

      {open ? (
        <div className="flex flex-wrap items-center gap-2 mt-3">
          {primary.url ? (
            <button type="button" className="btn btn-main btn-sm" disabled={busy} onClick={onPrimary}>
              {primary.label}
            </button>
          ) : null}
          <button
            type="button"
            className={primary.url ? "btn btn-second btn-sm" : "btn btn-main btn-sm"}
            disabled={busy}
            onClick={onClose}
          >
            {closeLabel(task)}
          </button>
          {onSkip ? (
            <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={onSkip}>
              Skip
            </button>
          ) : null}
          <button
            type="button"
            className="btn btn-ghost btn-sm ml-auto"
            disabled={busy}
            onClick={() => setDeclining((v) => !v)}
          >
            Decline…
          </button>
        </div>
      ) : null}

      {open && declining ? (
        <div className="mt-3">
          <textarea
            className="input w-full"
            rows={2}
            aria-label="Why you decline"
            placeholder={task.agent_slug ? "Why – the seat reads this." : "Why – kept in the task's history."}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <div className="flex gap-2 mt-2">
            <button
              type="button"
              className="btn btn-second btn-sm"
              disabled={!reason.trim() || busy}
              onClick={() => void sendDecline()}
            >
              Decline
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDeclining(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
